import fitnessStyles from "../../styles/template-fitness.css?url";
import templateDemoStyles from "../../styles/template-demo.css?url";
import interactionStyles from "../../styles/page-interactions.css?url";
import editorialStyles from "../../styles/template-editorial.css?url";
import popupStyles from "../../styles/template-popup.css?url";
import { walkPageNodes } from "../../../../shared/src/page-tree";
import type { TemplatePreview } from "../../server/template-preview.server";

// Inside the setup screen, the example name stays out of sight until the chosen one is in.
const namingScript = `try {
  if (parent !== window && parent.drateSetupPreview)
    document.documentElement.classList.add('preview-naming');
} catch {
  // Opened on its own or elsewhere, the page shows its example name.
}`;

const namingStyle = `.preview-naming
  :is(
    [data-preview-name],
    [data-preview-text],
    [data-preview-image],
    [data-preview-initials],
    .membership-byline,
    .membership-avatar,
    .spotlight-monogram
  ) {
  visibility: hidden;
}`;

// Onboarding's theme changes cross-fade (onboarding-script.ts). Links open their page at once, as
// on a creator's site, without fading the whole page.
const themeFadeStyle = `::view-transition-old(root),
::view-transition-new(root) {
  animation-duration: 280ms;
  animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
}
@media (prefers-reduced-motion: reduce) {
  ::view-transition-old(root),
  ::view-transition-new(root) { animation: none; }
}`;

// The example runs no client app, so each link loads a whole page. Browsers that support it
// (Chrome, Edge) draw a link's page while the pointer rests on it, and the click shows it at once.
// Links that open elsewhere (Made with Drate, image credits) carry a target and are left alone.
const speculationRules = JSON.stringify({
  prerender: [
    {
      where: { and: [{ href_matches: "/*" }, { not: { selector_matches: "[target]" } }] },
      eagerness: "moderate",
    },
  ],
});

/**
 * A template preview's head ([...page].astro): never indexed, Drate's icon, the naming script
 * that runs before the first paint, a render block until the page's last element is parsed, the
 * theme fade, the rules that prepare a hovered link's page, and the example's one stylesheet.
 */
export function templatePreviewHead(preview: TemplatePreview | undefined) {
  if (!preview) return {};
  const interactive =
    preview.home &&
    walkPageNodes(preview.home.document).some(
      (node) => node.kind === "tabs" || node.kind === "accordion" || node.kind === "component",
    );
  const motion =
    preview.templateId === "editorial-publication" &&
    preview.base &&
    preview.home &&
    walkPageNodes(preview.home.document).some((node) => node.motion);
  return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width,initial-scale=1" },
      { name: "robots", content: "noindex" },
      { title: preview.title },
    ],
    links: [
      { rel: "icon", href: "/favicon.svg" },
      { rel: "expect", href: "#preview-ready", blocking: "render" } as {
        rel: string;
        href: string;
      },
      { rel: "stylesheet", href: templateDemoStyles },
      ...(preview.header ? [{ rel: "stylesheet", href: editorialStyles }] : []),
      ...(interactive ? [{ rel: "stylesheet", href: interactionStyles }] : []),
      ...(preview.popup ? [{ rel: "stylesheet", href: popupStyles }] : []),
      ...(preview.templateId === "fitness-performance"
        ? [{ rel: "stylesheet", href: fitnessStyles }]
        : []),
    ],
    styles: [
      { children: namingStyle },
      ...(preview.templateId === "fitness-performance" ? [] : [{ children: themeFadeStyle }]),
      // No React app runs here. The UI kit progressively enhances the server's tabs;
      // without its small controls module, every panel remains readable.
      ...(interactive
        ? [
            {
              children:
                ".ui-tabs:not([data-ui-enhanced]) > .ui-tabs-list{display:none}.ui-tabs:not([data-ui-enhanced]) > .ui-tabs-panel[hidden]{display:block}",
            },
          ]
        : []),
      // A new theme's faces, when the preview draws its starter.
      ...(preview.faceCss ? [{ children: preview.faceCss }] : []),
    ],
    scripts: [
      { children: namingScript },
      { type: "speculationrules", children: speculationRules },
      ...(motion
        ? [
            {
              children:
                "document.documentElement.dataset.motion='ready';setTimeout(()=>{if(document.documentElement.dataset.motion==='ready')delete document.documentElement.dataset.motion},2500)",
            },
          ]
        : []),
    ],
  };
}
